import {
  IonModal,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonButton,
  IonContent,
  IonSpinner,
  IonText,
  IonIcon,
  useIonToast,
} from '@ionic/react';
import { copyOutline, refreshOutline } from 'ionicons/icons';
import { useEffect } from 'react';
import { useQuery, useMutation } from '@apollo/client/react';
import { GET_SECRET_CODE } from '@/graphql/queries/secretCode';
import { ROTATE_SECRET_CODE } from '@/graphql/mutations/leagueAdmin';
import type {
  GetSecretCodeQuery,
  GetSecretCodeQueryVariables,
  RotateSecretCodeMutation,
  RotateSecretCodeMutationVariables,
} from '../../../../types/graphql-generated';
import { closeOutline } from 'ionicons/icons';

interface SecretCodeModalProps {
  isOpen: boolean;
  leagueId: string;
  onClose: () => void;
}

export const SecretCodeModal = ({ isOpen, leagueId, onClose }: SecretCodeModalProps) => {
  const [presentToast] = useIonToast();

  const {
    data,
    loading,
    error
  } = useQuery<GetSecretCodeQuery, GetSecretCodeQueryVariables>(GET_SECRET_CODE, {
    variables: { id: leagueId },
    skip: !isOpen,
  });

  const [rotateSecretCode, { loading: rotateLoading }] = useMutation<
    RotateSecretCodeMutation,
    RotateSecretCodeMutationVariables
  >(ROTATE_SECRET_CODE, {
    refetchQueries: [{ query: GET_SECRET_CODE, variables: { id: leagueId } }],
    onCompleted: (res) => {
      const errors = res.rotateSecretCode?.errors ?? [];
      if (errors.length === 0) {
        presentToast({
          message: 'Código secreto actualizado',
          duration: 2000,
          position: 'bottom',
          positionAnchor: 'app-footer',
          cssClass: 'toast-success',
          buttons: [{ icon: closeOutline, role: 'cancel' }],
        });
      } else {
        presentToast({
          message: errors[0] ?? 'Error al cambiar el código secreto',
          duration: 3000,
          position: 'bottom',
          positionAnchor: 'app-footer',
          cssClass: 'toast-danger',
          buttons: [{ icon: closeOutline, role: 'cancel' }],
        });
      }
    },
    onError: (err) => {
      presentToast({
        message: `Error: ${err.message}`,
        duration: 3000,
        position: 'bottom',
        positionAnchor: 'app-footer',
        cssClass: 'toast-danger',
        buttons: [{ icon: closeOutline, role: 'cancel' }],
      });
    },
  });

  const secretCode = data?.fantasyLeague?.secretCode ?? '';
  const hasCode = secretCode.trim().length > 0;

  useEffect(() => {
    if (!error) return;
    if (!isOpen) return;

    presentToast({
      message: `Error: ${error.message}`,
      duration: 3000,
      position: 'bottom',
      positionAnchor: 'app-footer',
      cssClass: 'toast-danger',
      buttons: [{ icon: closeOutline, role: 'cancel' }],
    });
  }, [error, isOpen, presentToast, onClose]);

  const handleCopy = async () => {
    if (!hasCode) return;
    try {
      await navigator.clipboard.writeText(secretCode);
      presentToast({
        message: 'Código secreto copiado',
        duration: 1500,
        position: 'bottom',
        positionAnchor: 'app-footer',
        cssClass: 'toast-success',
        buttons: [{ icon: closeOutline, role: 'cancel' }],
      });
    } catch {
      presentToast({
        message: 'No se pudo copiar el código',
        duration: 2000,
        position: 'bottom',
        positionAnchor: 'app-footer',
        cssClass: 'toast-danger',
        buttons: [{ icon: closeOutline, role: 'cancel' }],
      });
    }
  };

  const handleRotate = () => {
    rotateSecretCode({ variables: { input: { fantasyLeagueId: leagueId } } });
  };

  return (
    <IonModal isOpen={isOpen} onDidDismiss={onClose}>
      <IonHeader>
        <IonToolbar>
          <IonTitle className="ml-4">Código secreto</IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={onClose}>Cerrar</IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>

      <IonContent className="ion-padding">
        {loading || rotateLoading ? (
          <div className="flex justify-center items-center min-h-[200px]">
            <IonSpinner name="crescent" aria-label="Cargando código secreto" />
          </div>
        ) : hasCode ? (
          <div className="flex flex-col items-center justify-center min-h-[200px] text-center">
            <IonText color="medium">
              <p className="text-sm mb-3">El código secreto de la liga es</p>
            </IonText>

            <div className="flex items-center justify-center gap-3">
              <div
                className="text-3xl font-bold tracking-widest select-all"
                aria-label="Código secreto"
              >
                {secretCode}
              </div>

              <IonButton
                fill="clear"
                size="small"
                color="medium"
                onClick={handleCopy}
                aria-label="Copiar código secreto"
              >
                <IonIcon icon={copyOutline} slot="icon-only" />
              </IonButton>
            </div>

            <IonText color="medium">
              <p className="text-xs mt-3">
                Comparte este código para que otros se unan a la liga.
              </p>
            </IonText>

            <IonButton
              fill="outline"
              size="small"
              className="mt-6"
              onClick={handleRotate}
              aria-label="Cambiar código secreto"
            >
              <IonIcon icon={refreshOutline} slot="start" />
              Cambiar código secreto
            </IonButton>
          </div>
        ) : (
          <div className="flex justify-center items-center min-h-[200px]">
            <IonText color="medium">
              <p>No disponible</p>
            </IonText>
          </div>
        )}
      </IonContent>
    </IonModal>
  );
};
